import { useQuery } from '@tanstack/react-query'
import { trpc } from 'shared/api'
import { Label } from 'shared/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from 'shared/components/ui/select'
import { useTranslate } from 'shared/lib/i18n'
import { placeTypeOf } from 'shared/lib/roles'
import { useLocationScope } from 'shared/store/location-scope'

/**
 * The Point field of a point-scoped screen.
 *
 * For an `admin` it is a selector over every Location, writing the shared choice.
 * For a bound role it is not a control at all: it names the place the account is
 * bound to (ADR-0029), and never asks for the Location list, which only an admin
 * may read.
 */
export function PointSelector({
	id,
	label,
	placeholder,
	testId,
	className = 'grid gap-1.5',
}: {
	id: string
	label: string
	placeholder?: string
	testId?: string
	className?: string
}) {
	const { t } = useTranslate()
	const { isBound, canPick, locationId, locationCode, setLocationId } =
		useLocationScope()
	const locationsQuery = useQuery({
		...trpc.locations.List.queryOptions(),
		enabled: canPick,
	})
	// A Point is asked for here, so a hub or the central cashbox is never offered.
	const locations = (locationsQuery.data ?? []).filter(
		(l) => placeTypeOf(l) === 'point',
	)
	// With no items the popup would open as an empty, invisible box and look like
	// the control is dead, so it always says why the list is empty.
	const emptyReason = locationsQuery.isPending
		? t('point.loading')
		: locationsQuery.isError
			? t('point.loadFailed')
			: t('point.none')

	if (isBound) {
		return (
			<div className={className}>
				<Label>{label}</Label>
				{locationId ? (
					<p className='flex h-9 items-center text-sm font-medium' data-testid='point-bound'>
						{locationCode ?? '—'}
					</p>
				) : (
					<p
						className='flex min-h-9 items-center text-sm text-muted-foreground'
						data-testid='point-unbound'
					>
						{t('point.unbound')}
					</p>
				)}
			</div>
		)
	}

	return (
		<div className={className}>
			<Label htmlFor={id}>{label}</Label>
			<Select value={locationId ?? undefined} onValueChange={setLocationId}>
				<SelectTrigger id={id} data-testid={testId}>
					<SelectValue placeholder={placeholder} />
				</SelectTrigger>
				<SelectContent position='popper' align='start'>
					{locations.length === 0 ? (
						<p
							className='px-2 py-1.5 text-sm text-muted-foreground'
							data-testid='point-options-empty'
						>
							{emptyReason}
						</p>
					) : (
						locations.map((location) => (
							<SelectItem key={location._id} value={location._id}>
								{location.id}
							</SelectItem>
						))
					)}
				</SelectContent>
			</Select>
		</div>
	)
}
